/**
 * Source/destination FileLocation leg selection for workflow step wizards.
 *
 * Mirrors the rules in EditWorkflowTabView (PUSH ~863-958, PULL ~959-1041).
 * Templates use this helper for load/save; live workflows still inline the matrix
 * until a future consolidation ticket.
 */
import { stringComparison } from "./app-utils";

export type FileInfoLeg = "source" | "destination";

/**
 * Storage types that authenticate via runtime IAM / service account — no per-step
 * credentialPlaceholders (mirrors API TemplateProxy.StorageTypesWithoutCredentials).
 */
export const STORAGE_TYPES_WITHOUT_STEP_CREDENTIALS = new Set(["gcs", "amazons3"]);

/** True when the step endpoint may carry template credential placeholders (host, port, etc.). */
export function storageTypeUsesStepCredentials(storageType: string | undefined | null): boolean {
  const st = String(storageType ?? "").toLowerCase().trim();
  if (!st) {
    return false;
  }
  return !STORAGE_TYPES_WITHOUT_STEP_CREDENTIALS.has(st);
}

const CREDENTIAL_LOCATION_KEYS = [
  "secretName",
  "SecretName",
  "secretValue",
  "SecretValue",
  "credentialPlaceholders",
  "CredentialPlaceholders",
  "authType",
  "AuthType",
] as const;

function omitCredentialFieldsFromLocation(loc: Record<string, any>): Record<string, any> {
  const out = { ...loc };
  for (const key of CREDENTIAL_LOCATION_KEYS) {
    delete out[key];
  }
  return out;
}

function readMergedCredentialMap(flat: NestFileInfoInput): Record<string, any> {
  if (flat.credentialPlaceholders && typeof flat.credentialPlaceholders === "object") {
    return { ...flat.credentialPlaceholders };
  }
  if (flat.secretValue && typeof flat.secretValue === "object") {
    return { ...flat.secretValue };
  }
  return {};
}

/**
 * Step-function secrets that are not endpoint credentials (host/port/etc.).
 * For IAM storage types these are preserved on the file leg while endpoint creds are stripped.
 */
function buildStepFunctionSecretFields(
  flat: NestFileInfoInput,
  functionName: string
): Record<string, any> | null {
  if (!stringComparison(functionName, "fileencrypt")) {
    return null;
  }

  const merged = readMergedCredentialMap(flat);
  const pgpKey = merged.pgpKey ?? merged.PGPKey ?? "";
  if (!pgpKey) {
    return null;
  }

  const secretValue = { pgpKey: String(pgpKey) };
  return {
    secretName: flat.secretName || "",
    secretValue,
    credentialPlaceholders: secretValue,
  };
}

/** Which nested FileLocation leg supplies page-2 file fields (storage, path, name, rename). */
export function getFileFieldsLeg(transferType: string, functionName: string): FileInfoLeg {
  if (stringComparison(transferType, "PULL")) {
    if (
      stringComparison(functionName, "filedownload") ||
      stringComparison(functionName, "filedelete")
    ) {
      return "source";
    }
    return "destination";
  }

  if (stringComparison(transferType, "PUSH")) {
    if (stringComparison(functionName, "filedelete")) {
      return "source";
    }
    return "destination";
  }

  // Unknown transfer type: preserve legacy template behavior (destination).
  return "destination";
}

function pairsFromRenameList(raw: unknown): Record<string, string> {
  if (!Array.isArray(raw)) {
    return {};
  }
  const out: Record<string, string> = {};
  for (const item of raw) {
    try {
      const pair = typeof item === "string" ? JSON.parse(item) : item;
      if (Array.isArray(pair) && pair.length >= 2) {
        out[String(pair[0])] = String(pair[1] ?? "");
      }
    } catch {
      // skip non-pair entries
    }
  }
  return out;
}

/** Dictionary only — `[]` / PascalCase-only / undefined / Map must not wipe the wizard map. */
export function coerceFileRenameMap(raw: unknown): Record<string, string> {
  if (raw instanceof Map) {
    const out: Record<string, string> = {};
    for (const [k, v] of raw.entries()) {
      out[String(k)] = String(v ?? "");
    }
    return out;
  }
  if (!raw || typeof raw !== "object" || Array.isArray(raw)) {
    return {};
  }
  const out: Record<string, string> = {};
  for (const [k, v] of Object.entries(raw as Record<string, unknown>)) {
    out[String(k)] = String(v ?? "");
  }
  return out;
}

export function readFileRenameFromLocation(
  info: Record<string, any> | null | undefined
): Record<string, string> {
  const camel = coerceFileRenameMap(info?.fileRename);
  if (Object.keys(camel).length > 0) {
    return camel;
  }
  const pascal = coerceFileRenameMap(info?.FileRename);
  if (Object.keys(pascal).length > 0) {
    return pascal;
  }
  return pairsFromRenameList(info?.fileRenameList);
}

export function readBucketRenameFromLocation(
  info: Record<string, any> | null | undefined
): string {
  const v = info?.bucketRename ?? info?.BucketRename;
  return v == null ? "" : String(v);
}

export function readFileMovePatternFromStep(
  step: Record<string, any> | null | undefined
): string[] {
  const pascal = Array.isArray(step?.FileMovePattern)
    ? step.FileMovePattern.map((x: unknown) => String(x))
    : [];
  if (pascal.length > 0) {
    return pascal;
  }
  const camel = Array.isArray(step?.fileMovePattern)
    ? step.fileMovePattern.map((x: unknown) => String(x))
    : [];
  if (camel.length > 0) {
    return camel;
  }
  const display = Array.isArray(step?.dataFileList)
    ? step.dataFileList.map((x: unknown) => String(x)).filter((x) => x !== "")
    : [];
  return display.map((name) =>
    name.startsWith("*") ? name : "*".concat(name.replace("/", "\\/"))
  );
}

export function readFileRenameFromStep(
  rawStep: Record<string, any> | null | undefined,
  transferType: string,
  functionName?: string
): Record<string, string> {
  if (!rawStep) {
    return {};
  }
  const flat = readFileRenameFromLocation(rawStep);
  if (Object.keys(flat).length > 0) {
    return flat;
  }
  const fn = functionName ?? rawStep.functionName ?? rawStep.FunctionName ?? "";
  return readFileRenameFromLocation(
    pickLegInfo(rawStep, getFileFieldsLeg(transferType, fn))
  );
}

export function readBucketRenameFromStep(
  rawStep: Record<string, any> | null | undefined,
  transferType: string,
  functionName?: string
): string {
  if (!rawStep) {
    return "";
  }
  const flat = readBucketRenameFromLocation(rawStep);
  if (flat) {
    return flat;
  }
  const fn = functionName ?? rawStep.functionName ?? rawStep.FunctionName ?? "";
  return readBucketRenameFromLocation(
    pickLegInfo(rawStep, getFileFieldsLeg(transferType, fn))
  );
}

function readPathString(info: Record<string, any> | null | undefined): string {
  if (!info) {
    return "";
  }
  const value = info.path ?? info.Path;
  return value == null ? "" : String(value);
}

/**
 * Flat wizard path when it has text; otherwise the file-leg path (then stepConfiguration / destinationPath).
 * A blank flat path must not replace a nested bucket path that still holds a placeholder.
 */
export function readStepFilePath(
  rawStep: Record<string, any> | null | undefined,
  transferType: string,
  functionName?: string
): string {
  if (!rawStep) {
    return "";
  }
  const flat = readPathString(rawStep);
  if (flat.trim()) {
    return flat;
  }
  const fn = functionName ?? rawStep.functionName ?? rawStep.FunctionName ?? "";
  const fromLeg = readPathString(pickLegInfo(rawStep, getFileFieldsLeg(transferType, fn)));
  if (fromLeg.trim()) {
    return fromLeg;
  }
  const config = rawStep.stepConfiguration || rawStep.StepConfiguration || {};
  const fromConfig = readPathString(config);
  if (fromConfig.trim()) {
    return fromConfig;
  }
  const destinationPath = rawStep.destinationPath;
  return destinationPath == null ? "" : String(destinationPath);
}

/** Which nested FileLocation leg supplies secrets (may differ from file fields for azure/amazons3remote). */
export function getSecretLeg(
  transferType: string,
  functionName: string,
  storageType: string
): FileInfoLeg {
  if (stringComparison(transferType, "PULL")) {
    if (
      stringComparison(functionName, "filedownload") ||
      stringComparison(functionName, "filedelete")
    ) {
      return "source";
    }
  }

  const st = String(storageType ?? "").toLowerCase();
  if (st === "azure" || st === "amazons3remote") {
    return "destination";
  }

  return "source";
}

function pickLegInfo(rawStep: Record<string, any>, leg: FileInfoLeg): Record<string, any> {
  if (leg === "source") {
    return rawStep.sourceFileInfo || rawStep.SourceFileInfo || {};
  }
  return rawStep.destinationFileInfo || rawStep.DestinationFileInfo || {};
}

function readField(info: Record<string, any>, camel: string, pascal: string): any {
  return info[camel] ?? info[pascal];
}

export interface FlattenedFileInfo {
  locationType: string;
  storageType: string;
  name: string;
  path: string;
  fileRename: Record<string, string> | any;
  bucketRename: string;
  secretName: string;
  secretValue: Record<string, any>;
  hostName: string;
  port: string;
  userName: string;
  password: string;
  pgpKey: string;
  connectionString: string;
  roleARN: string;
  region: string;
}

/**
 * Flatten nested sourceFileInfo/destinationFileInfo into wizard page-2 fields.
 * Uses transfer type + function name to pick the correct leg(s).
 */
export function flattenFileInfoFromStep(
  rawStep: Record<string, any>,
  transferType: string
): FlattenedFileInfo {
  const config = rawStep.stepConfiguration || rawStep.StepConfiguration || {};
  const destInfo = rawStep.destinationFileInfo || rawStep.DestinationFileInfo || {};

  const functionName = rawStep.functionName || rawStep.FunctionName || "";

  // Prefer already-flat top-level values when present (in-session edits).
  const hasFlatFileFields =
    !!(rawStep.locationType || rawStep.LocationType || rawStep.storageType || rawStep.StorageType);

  let fileInfo: Record<string, any>;
  let secretInfo: Record<string, any>;

  if (hasFlatFileFields) {
    fileInfo = rawStep;
    const storageType =
      rawStep.storageType || rawStep.StorageType || config.storageType || "";
    secretInfo = pickLegInfo(rawStep, getSecretLeg(transferType, functionName, storageType));
  } else {
    const fileLeg = getFileFieldsLeg(transferType, functionName);
    fileInfo = pickLegInfo(rawStep, fileLeg);
    const storageType =
      readField(fileInfo, "storageType", "StorageType") ||
      config.storageType ||
      rawStep.storageType ||
      "";
    secretInfo = pickLegInfo(rawStep, getSecretLeg(transferType, functionName, storageType));
  }

  const secretValue =
    readField(secretInfo, "credentialPlaceholders", "CredentialPlaceholders") ||
    readField(secretInfo, "secretValue", "SecretValue") ||
    readField(destInfo, "credentialPlaceholders", "CredentialPlaceholders") ||
    readField(destInfo, "secretValue", "SecretValue") ||
    readField(config, "secretValue", "SecretValue") ||
    rawStep.secretValue ||
    {};

  const sv = secretValue && typeof secretValue === "object" ? secretValue : {};

  return {
    locationType:
      readField(fileInfo, "locationType", "LocationType") ||
      config.locationType ||
      rawStep.locationType ||
      "",
    storageType:
      readField(fileInfo, "storageType", "StorageType") ||
      config.storageType ||
      rawStep.storageType ||
      "",
    name:
      readField(fileInfo, "name", "Name") ||
      config.bucketName ||
      rawStep.name ||
      rawStep.destinationName ||
      "",
    path: readStepFilePath(rawStep, transferType, functionName),
    fileRename:
      readField(fileInfo, "fileRename", "FileRename") ||
      config.fileRename ||
      rawStep.fileRename ||
      {},
    bucketRename:
      readField(fileInfo, "bucketRename", "BucketRename") ||
      config.bucketName ||
      rawStep.bucketRename ||
      "",
    secretName:
      readField(secretInfo, "secretName", "SecretName") ||
      config.secretName ||
      rawStep.secretName ||
      "",
    secretValue: sv,
    hostName: sv.host || sv.Host || config.hostName || rawStep.hostName || "",
    port: sv.port || sv.Port || config.port || rawStep.port || "",
    userName: sv.userName || sv.UserName || config.userName || rawStep.userName || "",
    password: sv.password || sv.Password || config.password || rawStep.password || "",
    pgpKey: sv.pgpKey || sv.PGPKey || config.pgpKey || rawStep.pgpKey || "",
    connectionString:
      sv.connectionString ||
      sv.ConnectionString ||
      config.connectionString ||
      rawStep.connectionString ||
      "",
    roleARN: sv.roleARN || sv.RoleARN || config.roleARN || rawStep.roleARN || "",
    region: sv.region || sv.Region || config.region || rawStep.region || "",
  };
}

export interface NestFileInfoInput {
  locationType?: string;
  storageType?: string;
  path?: string;
  name?: string;
  fileRename?: Record<string, string> | any;
  bucketRename?: string;
  secretName?: string;
  secretValue?: Record<string, any>;
  credentialPlaceholders?: Record<string, any>;
  authType?: string;
}

function buildFileLocationFields(flat: NestFileInfoInput): Record<string, any> {
  const loc: Record<string, any> = {
    locationType: flat.locationType || "",
    storageType: flat.storageType || "",
    path: flat.path || "",
    name: flat.name || "",
    fileRename: flat.fileRename || {},
    bucketRename: flat.bucketRename || "",
  };

  if (flat.authType) {
    loc.authType = flat.authType;
  }

  return loc;
}

function buildSecretFields(flat: NestFileInfoInput): Record<string, any> {
  const merged =
    flat.credentialPlaceholders && typeof flat.credentialPlaceholders === "object"
      ? flat.credentialPlaceholders
      : flat.secretValue && typeof flat.secretValue === "object"
        ? flat.secretValue
        : {};

  return {
    secretName: flat.secretName || "",
    secretValue: merged,
    credentialPlaceholders: merged,
  };
}

/**
 * Write flat wizard page-2 fields back into sourceFileInfo and/or destinationFileInfo.
 * Preserves the leg that was not edited.
 */
export function nestFileInfoIntoStep(
  flat: NestFileInfoInput,
  transferType: string,
  functionName: string,
  existingStep: Record<string, any> = {}
): { sourceFileInfo: Record<string, any>; destinationFileInfo: Record<string, any> } {
  const existingSource = existingStep.sourceFileInfo || existingStep.SourceFileInfo || {};
  const existingDest =
    existingStep.destinationFileInfo || existingStep.DestinationFileInfo || {};

  const fileLeg = getFileFieldsLeg(transferType, functionName);
  const secretLeg = getSecretLeg(transferType, functionName, flat.storageType || "");

  const fileFields = buildFileLocationFields(flat);
  const secretFields = buildSecretFields(flat);

  let sourceFileInfo = { ...existingSource };
  let destinationFileInfo = { ...existingDest };

  const applyToLeg = (leg: FileInfoLeg, patch: Record<string, any>) => {
    if (leg === "source") {
      sourceFileInfo = { ...sourceFileInfo, ...patch };
    } else {
      destinationFileInfo = { ...destinationFileInfo, ...patch };
    }
  };

  applyToLeg(fileLeg, fileFields);

  if (!storageTypeUsesStepCredentials(flat.storageType)) {
    sourceFileInfo = omitCredentialFieldsFromLocation(sourceFileInfo);
    destinationFileInfo = omitCredentialFieldsFromLocation(destinationFileInfo);
    applyToLeg(fileLeg, fileFields);

    const stepFunctionSecrets = buildStepFunctionSecretFields(flat, functionName);
    if (stepFunctionSecrets) {
      applyToLeg(fileLeg, stepFunctionSecrets);
    }

    return { sourceFileInfo, destinationFileInfo };
  }

  if (secretLeg === fileLeg) {
    applyToLeg(fileLeg, secretFields);
  } else {
    applyToLeg(secretLeg, secretFields);
  }

  return { sourceFileInfo, destinationFileInfo };
}

/** True when top-level flat file fields are absent but nested legs may hold data. */
export function stepNeedsNestedFileInfoFallback(step: Record<string, any>): boolean {
  const hasFlat =
    !!(step.locationType || step.LocationType || step.storageType || step.StorageType);
  if (hasFlat) {
    return false;
  }
  const src = step.sourceFileInfo || step.SourceFileInfo;
  const dest = step.destinationFileInfo || step.DestinationFileInfo;
  return !!(src && Object.keys(src).length) || !!(dest && Object.keys(dest).length);
}
